import {resolvePremiumStatus, type User} from '@perfectpost/perfect-post-common';

import type {PremiumStatus} from '../../services/subscription';

/**
 * Statut premium de l'utilisateur, recalculé à partir des faits LOCAUX (le
 * profil `GET /me`), repris tel quel de `src/store/mainslice.ts`.
 *
 * Le verdict vient de la règle partagée avec l'API et l'extension
 * (`resolvePremiumStatus`).
 *
 * Le groupe Cognito `CUSTOMER` n'entre PLUS dans ce calcul : l'attribution
 * manuelle du premium est portée par `premiumGrantedAt`, un fait de la base,
 * et le groupe est voué à disparaître. Ce chemin local ne doit donc plus en
 * dépendre — sans quoi la suppression du groupe rendrait gratuits les comptes
 * offerts, en silence. Vérifié avant de couper, en production : aucun des 276
 * membres restants du groupe n'en dépend seul.
 *
 * `hasTeamPremiumLicense` est un fait serveur rapporté par
 * `GET /me/premium-status` (le payload de `GetMe` ne porte pas la licence
 * d'équipe) : `SessionProvider` le conserve pour ces recalculs synchrones.
 *
 * ⚠️ CE N'EST PAS UNE DÉRIVATION PURE DE L'ÉTAT SERVEUR
 *
 * La fonction prend un `existingStatus` : le statut précédent participe au
 * calcul du suivant (préserver `paused` quand le serveur ne réaffirme pas le
 * drapeau, préserver `premium` en dernier recours). C'est ce qui l'empêche
 * d'être un simple `select` de la requête, et ce qui justifie que
 * `SessionProvider` garde le statut dans un état React plutôt que de le
 * recalculer à chaque rendu.
 */
export const getPremiumStatus = ({
  user,
  hasTeamPremiumLicense,
  existingStatus,
}: {
  user?: User;
  hasTeamPremiumLicense?: boolean;
  existingStatus?: PremiumStatus;
}): PremiumStatus => {
  const resolved = resolvePremiumStatus({
    stripeSubscriptionId: user?.stripeSubscriptionId,
    subscriptionPaused: user?.subscriptionPaused,
    subscriptionPausedUntil: user?.subscriptionPausedUntil,
    earlyAdoptersPremiumExpireAt: user?.earlyAdoptersPremiumExpireAt,
    hasTeamPremiumLicense,
    // Le premium OFFERT vit en base. Sans ce fait, ce chemin ne connaissait
    // l'attribution manuelle QUE par le groupe Cognito — en cours de suppression.
    premiumGrantedAt: user?.premiumGrantedAt,
  });

  // A team premium license keeps premium access even while the personal
  // subscription is paused — nothing to preserve in that case.
  if (resolved.source === 'team') {
    return resolved.status;
  }

  // Preserve the paused state across getMe refreshes when the user payload
  // does not explicitly restate the flag (stale backend / partial update).
  // Only preserve when subscriptionPaused is undefined — if it's explicitly
  // false, the server is telling us the pause expired and we must let the
  // resolved status promote the user back to 'premium'.
  if (existingStatus === 'paused' && user?.subscriptionPaused === undefined) {
    return 'paused';
  }

  if (resolved.status === 'free' && existingStatus === 'premium') {
    return 'premium';
  }

  return resolved.status;
};
